import type { Product } from './types';

/**
 * What a product listing reads of a product: the card (name, code, photo, badge, connection icons, colour dots),
 * the in-page text filter (name, description, code) and the filter bar (connection, location, colour, panels,
 * availability, attributes, sub-filter tiles). The detail-page fields (specs, features, downloads, FAQ, …) make up
 * about nine tenths of a product and are left out, so a page's loader data stays small enough to be serialised
 * into server-rendered HTML. `specs` is left out too: only the `system` filter reads it, which listings don't offer.
 */
const LISTED_PRODUCT_KEYS = [
  'id',
  'name',
  'code',
  'desc',
  'image',
  'badge',
  'categoryLabel',
  'wireless',
  'wired',
  'subcategoryIds',
  'parentId',
  'childVariantIds',
  'colorVariantIds',
  'color',
  'compatibleWith',
  'discontinued',
  'installLocation',
  'attributeValues',
  'filterLabels',
] as const satisfies readonly (keyof Product)[];

/** A product as a listing needs it; still a `Product`, as every field left out is optional. */
export type ListedProduct = Pick<Product, (typeof LISTED_PRODUCT_KEYS)[number]>;

export function toListedProduct(product: Product): ListedProduct {
  const listed: Partial<Record<keyof Product, unknown>> = {};
  for (const key of LISTED_PRODUCT_KEYS) {
    if (product[key] !== undefined) {
      listed[key] = product[key];
    }
  }

  return listed as ListedProduct;
}
